// In production, VITE_API_URL points to the Railway backend.
// In local dev, Vite proxies /api to localhost:8000.
const BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('tourx_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${url}`, {
      headers: getAuthHeaders(),
      ...options,
    });
  } catch (err) {
    throw new Error(`Cannot reach API server. Is the backend running? (tried ${BASE}${url})`);
  }
  if (res.status === 401) {
    // Don't clear session for login/register endpoints — 401 there means wrong credentials
    const isAuthEndpoint = url === '/auth/login' || url === '/auth/register' || url === '/auth/pin-login';
    if (!isAuthEndpoint) {
      localStorage.removeItem('tourx_token');
      localStorage.removeItem('tourx_user');
      window.location.href = '/login';
      throw new Error('Session expired');
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = typeof body.detail === 'string' ? body.detail : (typeof body.detail === 'object' ? JSON.stringify(body.detail) : `API error: ${res.status} ${res.statusText}`);
    throw new Error(detail);
  }
  return res.json();
}

export const api = {
  // Auth
  register: (data: {
    email: string; password: string; first_name: string; last_name: string;
    company_name?: string; company_id?: number; phone?: string;
  }) => fetchJson<any>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),

  login: (email: string, password: string) =>
    fetchJson<any>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  getMe: () => fetchJson<any>('/auth/me'),

  changePassword: (current_password: string, new_password: string) =>
    fetchJson<any>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  listUsers: () => fetchJson<any[]>('/auth/users'),

  updateUser: (id: number, data: any) =>
    fetchJson<any>(`/auth/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  inviteUser: (data: {
    email: string; password: string; first_name: string; last_name: string;
    role?: string; phone?: string;
  }) => fetchJson<any>('/auth/users/invite', { method: 'POST', body: JSON.stringify(data) }),

  deleteUser: (id: number) =>
    fetchJson<any>(`/auth/users/${id}`, { method: 'DELETE' }),

  resetUserPassword: (id: number) =>
    fetchJson<any>(`/auth/users/${id}/reset-password`, { method: 'POST' }),

  suspendUser: (id: number) =>
    fetchJson<any>(`/auth/users/${id}/suspend`, { method: 'POST' }),

  activateUser: (id: number) =>
    fetchJson<any>(`/auth/users/${id}/activate`, { method: 'POST' }),

  getDriversForLinking: () => fetchJson<any[]>('/auth/drivers-for-linking'),

  listCompanies: () => fetchJson<any[]>('/auth/companies'),

  createCompany: (data: any) =>
    fetchJson<any>('/auth/companies', { method: 'POST', body: JSON.stringify(data) }),

  // Card reader
  listReaders: () => fetchJson<{ readers: string[]; pyscard_installed: boolean; count: number }>('/cards/readers'),
  connectReader: (index = 0) =>
    fetchJson<any>(`/cards/connect?reader_index=${index}`, { method: 'POST' }),
  readCard: () => fetchJson<any>('/cards/read', { method: 'POST' }),
  diagnose: (index = 0) => fetchJson<any>(`/cards/diagnose?reader_index=${index}`),
  readRaw: () => fetchJson<any>('/cards/read-raw'),
  readDump: () => fetchJson<any>('/cards/read-dump', { method: 'POST' }),
  disconnectReader: () => fetchJson<any>('/cards/disconnect', { method: 'POST' }),

  // Drivers (with company-based permissions) — paginated
  getDrivers: (params?: { search?: string; depot_id?: number; manager_id?: number; page?: number; page_size?: number; sort_by?: string; sort_dir?: string }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.depot_id != null) qs.set('depot_id', String(params.depot_id))
    if (params?.manager_id != null) qs.set('manager_id', String(params.manager_id))
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    if (params?.sort_by) qs.set('sort_by', params.sort_by)
    if (params?.sort_dir) qs.set('sort_dir', params.sort_dir)
    const q = qs.toString()
    return fetchJson<any>(`/drivers${q ? `?${q}` : ''}`)
  },
  createDriver: (data: any) => fetchJson<any>('/drivers', { method: 'POST', body: JSON.stringify(data) }),
  updateDriver: (driverId: number, data: any) => fetchJson<any>(`/drivers/${driverId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDriver: (driverId: number) => fetchJson<any>(`/drivers/${driverId}`, { method: 'DELETE' }),
  getDriverAnalysis: (driverId: number) => fetchJson<any>(`/drivers/${driverId}/analysis`),
  getDriverDownloads: (driverId: number) => fetchJson<any[]>(`/drivers/${driverId}/downloads`),
  exportDriversCsv: (params?: { search?: string; depot_id?: number }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.depot_id != null) qs.set('depot_id', String(params.depot_id))
    const q = qs.toString()
    const token = localStorage.getItem('tourx_token')
    const BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api'
    window.open(`${BASE}/drivers/export/csv${q ? `?${q}` : ''}${token ? `${q ? '&' : '?'}token=${encodeURIComponent(token)}` : ''}`, '_blank')
  },
  getManagers: () => fetchJson<any[]>('/drivers/managers/list'),
  assignManager: (driverId: number, managerId: number | null) =>
    fetchJson<any>(`/drivers/${driverId}/assign-manager`, {
      method: 'PUT',
      body: JSON.stringify({ manager_id: managerId }),
    }),

  // Depots
  getDepots: () => fetchJson<any[]>('/depots'),
  createDepot: (data: any) =>
    fetchJson<any>('/depots', { method: 'POST', body: JSON.stringify(data) }),
  updateDepot: (id: number, data: any) =>
    fetchJson<any>(`/depots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDepot: (id: number) =>
    fetchJson<any>(`/depots/${id}`, { method: 'DELETE' }),
  assignDriversToDepot: (ids: number[], depotId: number | null) =>
    fetchJson<any>('/depots/assign-drivers', { method: 'PUT', body: JSON.stringify({ ids, depot_id: depotId }) }),
  assignVehiclesToDepot: (ids: number[], depotId: number | null) =>
    fetchJson<any>('/depots/assign-vehicles', { method: 'PUT', body: JSON.stringify({ ids, depot_id: depotId }) }),

  // Dashboard
  getDashboardStats: () => fetchJson<any>('/dashboard/stats'),
  getDashboardActivity: () => fetchJson<any[]>('/dashboard/activity'),
  getOperations: () => fetchJson<any>('/dashboard/operations'),
  getCompliance: () => fetchJson<any>('/dashboard/compliance'),

  // Infringement notes
  getInfringementNotes: (driverId: number) => fetchJson<any[]>(`/infringements/notes/${driverId}`),
  addInfringementNote: (data: {
    driver_id: number; infringement_date: string; infringement_type: string; note_text: string;
    severity?: string; regulation?: string;
  }) => fetchJson<any>('/infringements/notes', { method: 'POST', body: JSON.stringify(data) }),
  updateInfringementNote: (noteId: number, data: {
    status?: string; note_text?: string; driver_explanation?: string; dcrs_points_applied?: boolean;
  }) => fetchJson<any>(`/infringements/notes/${noteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInfringementNote: (noteId: number) =>
    fetchJson<any>(`/infringements/notes/${noteId}`, { method: 'DELETE' }),
  getInfringementStats: () => fetchJson<any>('/infringements/stats'),

  // Fleet vehicles
  getVehicles: () => fetchJson<any[]>('/vehicles'),
  getVehicle: (id: number) => fetchJson<any>(`/vehicles/${id}`),
  getVehicleActivity: (id: number) => fetchJson<any>(`/vehicles/${id}/activity`),
  getVehicleCompliance: () => fetchJson<any>('/vehicles/compliance/overview'),
  getFleetStats: () => fetchJson<any>('/vehicles/stats/overview'),
  createVehicle: (data: any) =>
    fetchJson<any>('/vehicles', { method: 'POST', body: JSON.stringify(data) }),
  updateVehicle: (id: number, data: any) =>
    fetchJson<any>(`/vehicles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteVehicle: (id: number) =>
    fetchJson<any>(`/vehicles/${id}`, { method: 'DELETE' }),
  permanentDeleteVehicle: (id: number) =>
    fetchJson<any>(`/vehicles/${id}/permanent`, { method: 'DELETE' }),
  reactivateVehicle: (id: number) =>
    fetchJson<any>(`/vehicles/${id}/reactivate`, { method: 'PUT' }),
  getInactiveVehicles: () => fetchJson<any[]>('/vehicles/inactive/list'),

  // PMI records
  getVehiclePMIs: (vehicleId: number) => fetchJson<any[]>(`/vehicles/${vehicleId}/pmi`),
  createPMI: (vehicleId: number, data: any) =>
    fetchJson<any>(`/vehicles/${vehicleId}/pmi`, { method: 'POST', body: JSON.stringify(data) }),
  updatePMI: (pmiId: number, data: any) =>
    fetchJson<any>(`/vehicles/pmi/${pmiId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePMI: (pmiId: number) =>
    fetchJson<any>(`/vehicles/pmi/${pmiId}`, { method: 'DELETE' }),

  // Worker Calendar
  getCalendar: (driverId: number, month?: string) =>
    fetchJson<any>(`/calendar/${driverId}${month ? `?month=${month}` : ''}`),
  getMissingDays: (driverId: number, months = 3) =>
    fetchJson<any>(`/calendar/${driverId}/missing?months=${months}`),
  addLeave: (data: { driver_id: number; date: string; leave_type: string; notes?: string }) =>
    fetchJson<any>('/calendar/leave', { method: 'POST', body: JSON.stringify(data) }),
  addLeaveBulk: (data: { driver_id: number; date_from: string; date_to: string; leave_type: string; notes?: string }) =>
    fetchJson<any>('/calendar/leave/bulk', { method: 'POST', body: JSON.stringify(data) }),
  deleteLeave: (leaveId: number) =>
    fetchJson<any>(`/calendar/leave/${leaveId}`, { method: 'DELETE' }),

  // DCRS (Driver Compliance Reporting System)
  getDCRSConfig: () => fetchJson<any>('/dcrs/config'),
  updateDCRSConfig: (data: any) =>
    fetchJson<any>('/dcrs/config', { method: 'PUT', body: JSON.stringify(data) }),
  getDCRSDrivers: () => fetchJson<any[]>('/dcrs/drivers'),
  getDCRSDriver: (driverId: number) => fetchJson<any>(`/dcrs/drivers/${driverId}`),
  addDCRSPoints: (data: { driver_id: number; infringement_type: string; infringement_date: string; notes?: string }) =>
    fetchJson<any>('/dcrs/points', { method: 'POST', body: JSON.stringify(data) }),
  getDCRSProcedures: (status?: string) =>
    fetchJson<any[]>(`/dcrs/procedures${status ? `?status=${status}` : ''}`),
  updateDCRSProcedure: (id: number, data: { status: string; notes?: string }) =>
    fetchJson<any>(`/dcrs/procedures/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetAllDCRS: () =>
    fetchJson<any>('/dcrs/reset-all', { method: 'POST' }),

  // Infringement Letters
  getLetters: (driverId?: number) =>
    fetchJson<any[]>(`/letters/${driverId ? `?driver_id=${driverId}` : ''}`),
  getLetter: (id: number) => fetchJson<any>(`/letters/${id}`),
  createLetter: (data: {
    driver_id: number; period_start: string; period_end: string;
    infringements: any[]; send_email?: boolean; email_override?: string;
  }) => fetchJson<any>('/letters/', { method: 'POST', body: JSON.stringify(data) }),
  autoGenerateLetter: (driverId: number, periodStart?: string, periodEnd?: string) => {
    const params = new URLSearchParams();
    if (periodStart) params.set('period_start', periodStart);
    if (periodEnd) params.set('period_end', periodEnd);
    const qs = params.toString();
    return fetchJson<any>(`/letters/auto-generate/${driverId}${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  acknowledgeLetter: (letterId: number) =>
    fetchJson<any>(`/letters/acknowledge/${letterId}`, { method: 'POST' }),

  downloadLetterPdf: async (letterId: number, _filename?: string) => {
    // Direct browser navigation with token in URL — bypasses CORS entirely
    const token = localStorage.getItem('tourx_token');
    const url = `${BASE}/letters/download-pdf/${letterId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    window.open(url, '_blank');
  },

  // WTD Analysis
  getWTDAnalysis: (driverId: number, weeks = 17) =>
    fetchJson<any>(`/wtd/${driverId}?weeks=${weeks}`),
  getWTDOverview: () => fetchJson<any[]>('/wtd/company/overview'),

  // Scheduled Reports
  getReportTypes: () => fetchJson<any>('/reports/types'),
  getScheduledReports: () => fetchJson<any[]>('/reports/'),
  createScheduledReport: (data: {
    report_type: string; frequency: string;
    day_of_week?: number; day_of_month?: number;
    recipient_emails: string[];
  }) => fetchJson<any>('/reports/', { method: 'POST', body: JSON.stringify(data) }),
  updateScheduledReport: (id: number, data: any) =>
    fetchJson<any>(`/reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteScheduledReport: (id: number) =>
    fetchJson<any>(`/reports/${id}`, { method: 'DELETE' }),
  runReport: (id: number) =>
    fetchJson<any>(`/reports/${id}/run`, { method: 'POST' }),

  // Intelligence Engine — all accept optional depot_id filter
  getFleetRiskScore: (params?: { search?: string; depot_id?: number; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.depot_id != null) qs.set('depot_id', String(params.depot_id))
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    const q = qs.toString()
    return fetchJson<any>(`/intelligence/risk-score/fleet${q ? `?${q}` : ''}`)
  },
  getDriverRiskScore: (driverId: number) => fetchJson<any>(`/intelligence/risk-score/driver/${driverId}`),
  getFleetHoursRemaining: (params?: { search?: string; depot_id?: number; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.depot_id != null) qs.set('depot_id', String(params.depot_id))
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    const q = qs.toString()
    return fetchJson<any>(`/intelligence/hours-remaining${q ? `?${q}` : ''}`)
  },
  getDriverHoursRemaining: (driverId: number) => fetchJson<any>(`/intelligence/hours-remaining/${driverId}`),
  getMissingDataAlerts: (params?: { search?: string; depot_id?: number; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.depot_id != null) qs.set('depot_id', String(params.depot_id))
    if (params?.page) qs.set('page', String(params.page))
    const q = qs.toString()
    return fetchJson<any>(`/intelligence/missing-data${q ? `?${q}` : ''}`)
  },
  getDriverPatterns: (driverId: number) => fetchJson<any>(`/intelligence/patterns/${driverId}`),
  getFleetPatterns: (depotId?: number) => fetchJson<any>(`/intelligence/patterns${depotId != null ? `?depot_id=${depotId}` : ''}`),
  getFleetBenchmarks: (depotId?: number) => fetchJson<any>(`/intelligence/benchmarks${depotId != null ? `?depot_id=${depotId}` : ''}`),
  getComplianceCalendar: (depotId?: number) => fetchJson<any>(`/intelligence/compliance-calendar${depotId != null ? `?depot_id=${depotId}` : ''}`),
  getDriverDebrief: (driverId: number) => fetchJson<any>(`/intelligence/debrief/${driverId}`),
  getAuditPack: (depotId?: number) => fetchJson<any>(`/intelligence/audit-pack${depotId != null ? `?depot_id=${depotId}` : ''}`),

  // ── Quotes ──────────────────────────────────────────────────────────
  calculateQuote: (data: any) =>
    fetchJson<any>('/quotes/calculate', { method: 'POST', body: JSON.stringify(data) }),
  getQuotes: (params?: { status?: string; customer_id?: number; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.customer_id) qs.set('customer_id', String(params.customer_id))
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    const q = qs.toString()
    return fetchJson<any>(`/quotes/${q ? `?${q}` : ''}`)
  },
  getQuote: (id: number) => fetchJson<any>(`/quotes/${id}`),
  saveQuote: (data: any) =>
    fetchJson<any>('/quotes/', { method: 'POST', body: JSON.stringify(data) }),
  approveQuote: (id: number) =>
    fetchJson<any>(`/quotes/${id}/approve`, { method: 'POST' }),
  declineQuote: (id: number) =>
    fetchJson<any>(`/quotes/${id}/decline`, { method: 'POST' }),
  sendQuote: (id: number) =>
    fetchJson<any>(`/quotes/${id}/send`, { method: 'POST' }),
  getCustomers: () => fetchJson<any[]>('/quotes/customers'),
  getCustomerDetail: (id: number) => fetchJson<any>(`/quotes/customers/${id}`),
  createCustomer: (data: any) =>
    fetchJson<any>('/quotes/customers', { method: 'POST', body: JSON.stringify(data) }),
  updateCustomer: (id: number, data: any) =>
    fetchJson<any>(`/quotes/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getLatestFuelPrice: () => fetchJson<any>('/quotes/fuel-price'),
  addFuelPrice: (data: any) =>
    fetchJson<any>('/quotes/fuel-price', { method: 'POST', body: JSON.stringify(data) }),
  getFuelPriceHistory: () => fetchJson<any[]>('/quotes/fuel-price/history'),
  getQuoteConfig: () => fetchJson<any>('/quotes/config'),
  updateQuoteConfig: (data: any) =>
    fetchJson<any>('/quotes/config', { method: 'PUT', body: JSON.stringify(data) }),
  // ── Jobs ──────────────────────────────────────────────────────────
  getJobs: (params?: { status?: string; search?: string; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.search) qs.set('search', params.search)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    const q = qs.toString()
    return fetchJson<any>(`/jobs${q ? `?${q}` : ''}`)
  },
  getJobStats: () => fetchJson<any>('/jobs/stats'),
  getJob: (id: number) => fetchJson<any>(`/jobs/${id}`),
  updateJob: (id: number, data: any) =>
    fetchJson<any>(`/jobs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  bookInJob: (id: number, data: any) =>
    fetchJson<any>(`/jobs/${id}/book-in`, { method: 'POST', body: JSON.stringify(data) }),
  assignJob: (id: number, data: { assigned_vehicle_id: number; assigned_driver_id: number }) =>
    fetchJson<any>(`/jobs/${id}/assign`, { method: 'POST', body: JSON.stringify(data) }),
  updateJobStatus: (id: number, data: { status: string; notes?: string }) =>
    fetchJson<any>(`/jobs/${id}/status`, { method: 'POST', body: JSON.stringify(data) }),
  getJobBriefing: (id: number) => fetchJson<any>(`/jobs/${id}/briefing`),
  getAvailableVehicles: (jobDate: string, jobEndDate?: string) => {
    const qs = new URLSearchParams({ job_date: jobDate })
    if (jobEndDate) qs.set('job_end_date', jobEndDate)
    return fetchJson<any[]>(`/jobs/availability/vehicles?${qs}`)
  },
  getAvailableDrivers: (jobDate: string, jobEndDate?: string) => {
    const qs = new URLSearchParams({ job_date: jobDate })
    if (jobEndDate) qs.set('job_end_date', jobEndDate)
    return fetchJson<any[]>(`/jobs/availability/drivers?${qs}`)
  },
  getResourceBlocks: (resourceType?: string, resourceId?: number) => {
    const qs = new URLSearchParams()
    if (resourceType) qs.set('resource_type', resourceType)
    if (resourceId) qs.set('resource_id', String(resourceId))
    const q = qs.toString()
    return fetchJson<any[]>(`/jobs/resource-blocks${q ? `?${q}` : ''}`)
  },
  createResourceBlock: (data: any) =>
    fetchJson<any>('/jobs/resource-blocks', { method: 'POST', body: JSON.stringify(data) }),
  deleteResourceBlock: (id: number) =>
    fetchJson<any>(`/jobs/resource-blocks/${id}`, { method: 'DELETE' }),

  // ── Walkaround Checks ────────────────────────────────────────────────
  getWalkaroundStats: () => fetchJson<any>('/walkaround/stats'),
  getWalkaroundChecks: (params?: { vehicle_id?: number; driver_id?: number; date_from?: string; date_to?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.vehicle_id) qs.set('vehicle_id', String(params.vehicle_id))
    if (params?.driver_id) qs.set('driver_id', String(params.driver_id))
    if (params?.date_from) qs.set('date_from', params.date_from)
    if (params?.date_to) qs.set('date_to', params.date_to)
    if (params?.limit) qs.set('limit', String(params.limit))
    const q = qs.toString()
    return fetchJson<any[]>(`/walkaround/checks${q ? `?${q}` : ''}`)
  },
  getWalkaroundCheck: (id: number) => fetchJson<any>(`/walkaround/checks/${id}`),
  getLatestCheck: (vehicleId: number) => fetchJson<any>(`/walkaround/checks/latest/${vehicleId}`),
  getDriverChecks: (driverId: number, limit = 20) => fetchJson<any[]>(`/walkaround/checks/driver/${driverId}?limit=${limit}`),
  getWalkaroundDefects: (params?: { status_filter?: string; vehicle_id?: number; severity?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.vehicle_id) qs.set('vehicle_id', String(params.vehicle_id))
    if (params?.severity) qs.set('severity', params.severity)
    const q = qs.toString()
    return fetchJson<any[]>(`/walkaround/defects${q ? `?${q}` : ''}`)
  },
  getWalkaroundDefect: (id: number) => fetchJson<any>(`/walkaround/defects/${id}`),
  updateDefectStatus: (id: number, data: { status: string; notes?: string }) =>
    fetchJson<any>(`/walkaround/defects/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  addDefectNote: (id: number, notes: string) =>
    fetchJson<any>(`/walkaround/defects/${id}/notes`, { method: 'POST', body: JSON.stringify({ notes }) }),
  getWalkaroundConfig: () => fetchJson<any>('/walkaround/config'),
  updateWalkaroundConfig: (data: any) =>
    fetchJson<any>('/walkaround/config', { method: 'PUT', body: JSON.stringify(data) }),
  getTimingAnalysis: (checkId: number) => fetchJson<any>(`/walkaround/checks/${checkId}/timing-analysis`),
  getComplianceStats: (days = 30) => fetchJson<any>(`/walkaround/compliance-stats?days=${days}`),

  // ── Workshop / VMS ──────────────────────────────────────────────────
  getWorkshopJobs: (params?: { status?: string; vehicle_id?: number; provider_id?: number; job_type?: string; priority?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.vehicle_id) qs.set('vehicle_id', String(params.vehicle_id))
    if (params?.provider_id) qs.set('provider_id', String(params.provider_id))
    if (params?.job_type) qs.set('job_type', params.job_type)
    if (params?.priority) qs.set('priority', params.priority)
    const q = qs.toString()
    return fetchJson<any[]>(`/workshop/jobs${q ? `?${q}` : ''}`)
  },
  getWorkshopJob: (id: number) => fetchJson<any>(`/workshop/jobs/${id}`),
  createWorkshopJob: (data: any) =>
    fetchJson<any>('/workshop/jobs', { method: 'POST', body: JSON.stringify(data) }),
  updateWorkshopJob: (id: number, data: any) =>
    fetchJson<any>(`/workshop/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteWorkshopJob: (id: number) =>
    fetchJson<any>(`/workshop/jobs/${id}`, { method: 'DELETE' }),
  addWorkshopPart: (jobId: number, data: any) =>
    fetchJson<any>(`/workshop/jobs/${jobId}/parts`, { method: 'POST', body: JSON.stringify(data) }),
  removeWorkshopPart: (jobId: number, partId: number) =>
    fetchJson<any>(`/workshop/jobs/${jobId}/parts/${partId}`, { method: 'DELETE' }),
  getWorkshopProviders: () => fetchJson<any[]>('/workshop/providers'),
  createWorkshopProvider: (data: any) =>
    fetchJson<any>('/workshop/providers', { method: 'POST', body: JSON.stringify(data) }),
  updateWorkshopProvider: (id: number, data: any) =>
    fetchJson<any>(`/workshop/providers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getWorkshopDashboard: () => fetchJson<any>('/workshop/dashboard'),
  getVehicleServiceHistory: (vehicleId: number) => fetchJson<any[]>(`/workshop/vehicle/${vehicleId}/history`),
  createWorkshopFromDefect: (defectId: number) =>
    fetchJson<any>(`/workshop/from-defect/${defectId}`, { method: 'POST' }),
  getVehicleComponents: (vehicleId: number) => fetchJson<any[]>(`/workshop/vehicles/${vehicleId}/components`),
  addVehicleComponent: (vehicleId: number, data: any) =>
    fetchJson<any>(`/workshop/vehicles/${vehicleId}/components`, { method: 'POST', body: JSON.stringify(data) }),

  // ── Operations ──────────────────────────────────────────────────────
  getOpsBoard: (params?: { date_from?: string; date_to?: string; depot_id?: number }) => {
    const qs = new URLSearchParams()
    if (params?.date_from) qs.set('date_from', params.date_from)
    if (params?.date_to) qs.set('date_to', params.date_to)
    if (params?.depot_id) qs.set('depot_id', String(params.depot_id))
    const q = qs.toString()
    return fetchJson<any>(`/ops/allocation-board${q ? `?${q}` : ''}`)
  },
  allocateJob: (data: { job_id: number; vehicle_id: number; driver_id: number }) =>
    fetchJson<any>('/ops/allocate', { method: 'POST', body: JSON.stringify(data) }),
  dispatchJob: (jobId: number, notes?: string) =>
    fetchJson<any>(`/ops/dispatch/${jobId}`, { method: 'POST', body: JSON.stringify({ notes }) }),
  getLiveStatus: () => fetchJson<any[]>('/ops/live-status'),
  getLateAlerts: () => fetchJson<any[]>('/ops/late-alerts'),
  getActualVsPlanned: (days = 30) => fetchJson<any[]>(`/ops/actual-vs-planned?days=${days}`),
  getDriverDiary: () => fetchJson<any[]>('/ops/driver-app/diary'),
  driverAcceptJob: (jobId: number) =>
    fetchJson<any>(`/ops/driver-app/job/${jobId}/accept`, { method: 'POST' }),
  driverStartJob: (jobId: number, data?: any) =>
    fetchJson<any>(`/ops/driver-app/job/${jobId}/start`, { method: 'POST', body: JSON.stringify(data || {}) }),
  driverCompleteJob: (jobId: number, data?: any) =>
    fetchJson<any>(`/ops/driver-app/job/${jobId}/complete`, { method: 'POST', body: JSON.stringify(data || {}) }),
  updateDriverLocation: (data: any) =>
    fetchJson<any>('/ops/driver-app/location', { method: 'POST', body: JSON.stringify(data) }),

  // ── Invoicing & Accounts ────────────────────────────────────────────
  getInvoices: (params?: { status?: string; customer_id?: number; date_from?: string; date_to?: string; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.customer_id) qs.set('customer_id', String(params.customer_id))
    if (params?.date_from) qs.set('date_from', params.date_from)
    if (params?.date_to) qs.set('date_to', params.date_to)
    if (params?.page) qs.set('page', String(params.page))
    const q = qs.toString()
    return fetchJson<any>(`/invoices/${q ? `?${q}` : ''}`)
  },
  getInvoice: (id: number) => fetchJson<any>(`/invoices/${id}`),
  updateInvoice: (id: number, data: any) =>
    fetchJson<any>(`/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteInvoice: (id: number) =>
    fetchJson<any>(`/invoices/${id}`, { method: 'DELETE' }),
  generateInvoiceFromJobs: (data: { job_ids: number[]; po_number?: string; notes?: string }) =>
    fetchJson<any>('/invoices/generate-from-jobs', { method: 'POST', body: JSON.stringify(data) }),
  sendInvoice: (id: number) =>
    fetchJson<any>(`/invoices/${id}/send`, { method: 'POST' }),
  addPayment: (invoiceId: number, data: any) =>
    fetchJson<any>(`/invoices/${invoiceId}/payments`, { method: 'POST', body: JSON.stringify(data) }),
  getPayments: (invoiceId: number) => fetchJson<any[]>(`/invoices/${invoiceId}/payments`),
  createCreditNote: (invoiceId: number, data: any) =>
    fetchJson<any>(`/invoices/${invoiceId}/credit-note`, { method: 'POST', body: JSON.stringify(data) }),
  getCreditNotes: () => fetchJson<any[]>('/invoices/credit-notes/list'),
  getAgedDebt: () => fetchJson<any>('/invoices/accounts/aged-debt'),
  getRevenueSummary: (months = 6) => fetchJson<any>(`/invoices/accounts/revenue-summary?months=${months}`),
  getVatSummary: (dateFrom?: string, dateTo?: string) => {
    const qs = new URLSearchParams()
    if (dateFrom) qs.set('date_from', dateFrom)
    if (dateTo) qs.set('date_to', dateTo)
    const q = qs.toString()
    return fetchJson<any>(`/invoices/accounts/vat-summary${q ? `?${q}` : ''}`)
  },
  getCustomerStatement: (customerId: number) => fetchJson<any>(`/invoices/accounts/statements/${customerId}`),
  getInvoiceSettings: () => fetchJson<any>('/invoices/settings'),
  updateInvoiceSettings: (data: any) =>
    fetchJson<any>('/invoices/settings', { method: 'PATCH', body: JSON.stringify(data) }),

  // VU Downloads (DigiDown / TachoSafe device data)
  uploadVUFile: (filename: string, fileDataBase64: string) =>
    fetchJson<any>('/cards/upload-vu', {
      method: 'POST',
      body: JSON.stringify({ filename, file_data: fileDataBase64 }),
    }),

  uploadVUFiles: (files: { filename: string; file_data: string }[]) =>
    fetchJson<any>('/cards/upload-vu-bulk', {
      method: 'POST',
      body: JSON.stringify({ files }),
    }),

  listVUDownloads: () => fetchJson<any[]>('/cards/vu-downloads'),

  getVUDownload: (id: number) => fetchJson<any>(`/cards/vu-downloads/${id}`),

  deleteVUDownload: (id: number) =>
    fetchJson<any>(`/cards/vu-downloads/${id}`, { method: 'DELETE' }),

  deleteAllVUDownloads: () =>
    fetchJson<any>('/cards/vu-downloads', { method: 'DELETE' }),

  // Vehicle Tracking (RAM Tracking)
  getTrackingVehicles: () => fetchJson<any>('/tracking/vehicles'),

  // Health
  health: () => fetchJson<{ status: string }>('/health'),
};
